import { useState, useEffect } from 'react';
import FavoritesService from '../services/favoritesService';
import { useToast } from '../contexts/ToastContext';

const FavoriteButton = ({ movie, className = '', size = 'md' }) => {
  const { showToast } = useToast();
  const [isFavorite, setIsFavorite] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isAnimating, setIsAnimating] = useState(false);

  useEffect(() => {
    setIsFavorite(FavoritesService.isFavorite(movie.id));
  }, [movie.id]);

  const handleToggleFavorite = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (isLoading) return;
    
    console.log('FavoriteButton clicked:', movie); // Debug log
    
    setIsLoading(true);
    setIsAnimating(true);
    
    try {
      const newState = FavoritesService.toggleFavorite(movie);
      console.log('Toggle result:', newState); // Debug log
      setIsFavorite(newState);
      
      // Show toast notification
      showToast(
        newState 
          ? `"${movie.title}" added to favorites! ❤️` 
          : `"${movie.title}" removed from favorites`,
        newState ? 'success' : 'info'
      );
      
      // Trigger a custom event to notify other components
      window.dispatchEvent(new CustomEvent('favoritesChanged', {
        detail: { movieId: movie.id, isFavorite: newState }
      }));
      
      // Add visual feedback
      setTimeout(() => setIsAnimating(false), 600);
    } catch (error) {
      console.error('Error toggling favorite:', error);
      showToast('Error updating favorites', 'error');
    } finally {
      setIsLoading(false);
    }
  };

  const sizeClasses = {
    sm: 'w-8 h-8 text-sm',
    md: 'w-10 h-10 text-base',
    lg: 'w-12 h-12 text-lg'
  };

  return (
    <button
      onClick={handleToggleFavorite}
      disabled={isLoading}
      className={`
        ${sizeClasses[size]} 
        rounded-full flex items-center justify-center 
        transition-all duration-300 
        backdrop-blur-sm
        disabled:opacity-50 disabled:cursor-not-allowed
        ${isFavorite 
          ? 'bg-green-500/90 hover:bg-green-600 text-white shadow-lg shadow-green-500/30' 
          : 'bg-white/10 hover:bg-white/20 text-gray-300 hover:text-white'
        }
        border ${isFavorite ? 'border-green-400/30' : 'border-white/20'}
        hover:scale-110 active:scale-95
        ${isAnimating ? 'animate-pulse ring-4 ring-green-400/50 ring-opacity-75' : ''}
        ${className}
      `}
      aria-label={isFavorite ? 'Remove from favorites' : 'Add to favorites'}
      title={isFavorite ? 'Remove from favorites' : 'Add to favorites'}
    >
      {isLoading ? (
        <div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin"></div>
      ) : (
        <span className={`transition-all duration-300 ${isFavorite ? 'text-white scale-110' : ''} ${isAnimating ? 'scale-125' : ''}`}>
          {isFavorite ? '❤️' : '🤍'}
        </span>
      )}
    </button>
  );
};

export default FavoriteButton;
